import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, Stack, Button, Link } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useTemplateDetailsContext } from '../../../_shared/Details/context';
import useAssignedModules from '../../../_shared/_data_sources/useAssignedModules';
import useJurisdictions from 'data_sources/useJurisdictions';
import datetimeHelper from 'helpers/datetimeHelper';
import DetailList from '_lex/_atoms/DetailList';
import { AppContext } from 'views/App/context';

export default function TemplateDetails() {
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const { template, isChildTemplate } = useTemplateDetailsContext();
  const navigate = useNavigate();

  const assignedModulesQuery = useAssignedModules();
  const jurisdictionsQuery = useJurisdictions();
  const canManageTemplates = hasPermission('manageFieldBank', currentOrganizationId || '');

  const getAssignedModuleName = (moduleId: number) => {
    const module = assignedModulesQuery.data?.find((m: any) => m.id === moduleId);
    return module?.name || `Module ${moduleId}`;
  };

  const getJurisdictionName = (jurisdictionId: string) => {
    const jurisdiction = jurisdictionsQuery.data?.find((j: any) => j.id === jurisdictionId);
    return jurisdiction ? `${jurisdiction.name} (${jurisdiction.abbreviation})` : '--';
  };

  const formatDateOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '--';
    return datetimeHelper.formatDate(dateString);
  };

  const handleEditDetails = () => {
    navigate('edit');
  };

  const templateDetailsItems = [
    {
      label: t('performanceReporting.templates.details.fields.name'),
      value: template?.name || '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.assignedModule'),
      value: template?.assignedModuleId ? getAssignedModuleName(template.assignedModuleId) : '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.lastPublished'),
      value: template?.lastPublishDate ? formatDateOnly(template?.lastPublishDate) : '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.author'),
      value: template?.createdBy || '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.description'),
      value: template?.description || '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.effectiveDate'),
      value: formatDateOnly(template?.effectiveDate),
    },
    // Show jurisdiction only for child templates
    ...(isChildTemplate
      ? [
          {
            label: t('performanceReporting.templates.details.fields.jurisdiction'),
            value: template?.jurisdictionId ? getJurisdictionName(template.jurisdictionId) : '--',
          },
        ]
      : []),
    // Show parent template link only for child templates
    ...(isChildTemplate
      ? [
          {
            label: t('performanceReporting.templates.details.fields.parentTemplate'),
            value: (
              <Link href={`/admin/performance-reporting/templates/details/${template?.parentTemplateId}`}>
                {template?.parentTemplate?.name || 'Parent Template'}
              </Link>
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={12}>
      <Card className='lex-card'>
        <CardHeader
          title={t('performanceReporting.templates.details.sections.templateDetails')}
          action={
            canManageTemplates ? (
              <Button
                variant='contained'
                size='small'
                color='primary'
                onClick={handleEditDetails}
              >
                {t('performanceReporting.templates.details.actions.edit')}
              </Button>
            ) : null
          }
        />
        <CardContent>
          <DetailList items={templateDetailsItems} />
        </CardContent>
      </Card>
    </Stack>
  );
}

